<template>
  <div class="button-upload">
    <el-upload
      :class="uploadClass"
      action=""
      v-model:file-list="fileList"
      :auto-upload="false"
      :multiple="multiple"
      :accept="accept.join(',')"
      :before-upload="beforeUpload"
      :on-change="handleChange"
      :on-preview="handlePreview"
      :on-remove="handleRemove"
    >
      <el-button :type="buttonType" :size="buttonSize">
        <AliIcon name="upload"></AliIcon>
        <span>{{ buttonText }}</span>
      </el-button>
      <template #tip v-if="showTip">
        <div class="el-upload__tip">
          {{ tipText || `支持${getFormatTip()}文件，单个文件不超过${maxSize}MB` }}
        </div>
      </template>
    </el-upload>

    <!-- 图片预览对话框 -->
    <el-dialog v-model="dialogVisible" title="图片预览" width="98%" top="5vh" class="max-w-[800px]">
      <img :src="dialogImageUrl" class="preview-image w-full h-auto" alt="预览图片" />
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import { fileUtils } from '@/utils';
  import { getFormatTip as getCommonFormatTip, beforeUpload as commonBeforeUpload } from './uploadUtils';

  // 定义props
  interface Props {
    modelValue?: File[] | string[] | null;
    accept?: string[];
    maxSize?: number;
    multiple?: boolean;
    buttonText?: string;
    buttonType?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
    buttonSize?: 'large' | 'default' | 'small';
    tipText?: string;
    showTip?: boolean;
    uploadClass?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    accept: () => ['.docx', '.pdf', '.jpg', '.jpeg', '.png'],
    maxSize: 30,
    multiple: true,
    buttonText: '选择文件',
    buttonType: 'primary',
    buttonSize: 'default',
    tipText: '',
    showTip: true,
    uploadClass: ''
  });

  // 定义emits
  const emit = defineEmits<{
    'update:modelValue': [value: File[] | string[] | null];
    'change': [files: File[] | string[] | null];
    'preview': [file: File | string];
    'remove': [file: File | string];
  }>();

  // 文件列表状态
  const fileList = ref<Array<{name: string, url?: string, raw?: File}>>([]);

  // 预览弹窗相关状态
  const dialogVisible = ref(false);
  const dialogImageUrl = ref('');

  // 使用通用的文件格式提示文本函数，传入中文顿号作为分隔符
  const getFormatTip = () => getCommonFormatTip(props.accept, '、');

  // 监听modelValue变化，更新文件列表
  watch(() => props.modelValue, (newVal) => {
    if (newVal && Array.isArray(newVal)) {
      fileList.value = newVal.map(file => file);
    } else {
      fileList.value = [];
    }
  }, { immediate: true, deep: true });

  // 文件上传前校验 - 使用公共工具函数
    const beforeUpload = (file: File) => {
      return commonBeforeUpload(file, props.maxSize, props.accept, getFormatTip());
    };

  // 文件改变处理
  const handleChange = (file: any, fileList: any[]) => {
    const validFiles = fileList.filter(file => {
      const rawFile = file.raw || file;
      if (file.status === 'success' && file.url) return true;
      return beforeUpload(rawFile);
    });
    
    emit('update:modelValue', validFiles);
    emit('change', validFiles);
  };

  // 文件移除处理
  const handleRemove = (file: any) => {
    // 获取当前有效文件列表
    const currentFiles = fileList.value
      .filter(f => file.uid !== f.uid)
    
    emit('update:modelValue', currentFiles);
    emit('change', currentFiles);
    emit('remove', file.raw || file);
    
    return true;
  };

  // 文件预览处理
  const handlePreview = (file: any) => {
    const previewFile = file.raw || file.url;
    emit('preview', previewFile);

    // 根据文件类型处理预览
    if (fileUtils.isImageFile(previewFile)) {
      // 图片文件显示弹窗
      dialogImageUrl.value = fileUtils.getImagePreviewUrl(previewFile) ;
      dialogVisible.value = true;
    } else {
      // 非图片文件调用fileUtils下载
      fileUtils.downloadFile({
        file: previewFile,
        fileName: file.name
      });
    }
  };
</script>

<style scoped>
  .button-upload {
    display: inline-block;
    width: 100%;
  }

  /* 预览图片样式 */
  .preview-image {
    max-height: 80vh;
    object-fit: contain;
  }
</style>